import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '../ui/dialog';
import { Copy, CheckCheck, Plus, Replace, Loader2, ArrowUp, ArrowDown, Sparkles, Check, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { createMarkdownComponents, markdownComponents } from './markdownComponents';

/**
 * Card rendered under an assistant message that carries an edit_proposal
 * (T-025). Aplicar runs the client-side find/replace on the live editor via
 * onApplyProposal (which returns the number of replacements); Cancelar dismisses.
 * State is local to the message.
 */
const ProposalCard = ({ proposal, onApplyProposal, disabled }) => {
  const [applied, setApplied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className="mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3"
      data-testid="edit-proposal-card"
    >
      <p className="text-xs text-slate-700 dark:text-slate-200">
        La IA propone reemplazar <strong>"{proposal.find}"</strong> por{' '}
        <strong>"{proposal.replace}"</strong> ({proposal.matches_found} coincidencia(s))
        {proposal.sheet_name ? ` en ${proposal.sheet_name}` : ''}.
      </p>
      {applied ? (
        <p className="mt-2 text-xs text-green-700 dark:text-green-400 flex items-center gap-1">
          <Check className="w-3 h-3" /> Aplicado
        </p>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => {
              const count = onApplyProposal ? onApplyProposal(proposal) : 0;
              if (count > 0) setApplied(true);
            }}
            className="h-7 px-3 text-xs bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
            data-testid="apply-proposal"
            title={disabled ? 'Espera a que la IA termine' : undefined}
          >
            Aplicar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
            className="h-7 px-3 text-xs"
            data-testid="cancel-proposal"
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * ThinkingIndicator (T-145) — spinner con cronómetro mientras la IA trabaja.
 * Se monta sólo cuando `loading` es true, así el timer arranca en 0 cada vez.
 * El número que corre es la señal de que sigue viva (no colgada) en procesos
 * largos; tras 20s añade una nota de que operaciones largas pueden tardar.
 */
const ThinkingIndicator = () => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  return (
    <div className="flex justify-start">
      <div className="bg-white dark:bg-slate-800 shadow-sm px-4 py-2 rounded-2xl flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {secs < 20 ? 'Pensando' : 'Trabajando'}… <span className="tabular-nums text-slate-400">{mmss}</span>
        </p>
      </div>
    </div>
  );
};

/**
 * LiveActivity (T-204) — bloque colapsable que muestra en tiempo real las
 * líneas de actividad de herramientas mientras el job de IA corre. Se renderiza
 * bajo el ThinkingIndicator. Auto-scroll al fondo cuando crece. Monoespaciado
 * suave (text-xs font-mono, slate-400). data-testid="live-activity".
 */
const LiveActivity = ({ lines }) => {
  const scrollRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (scrollRef.current && !collapsed) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, collapsed]);

  return (
    <div className="flex justify-start">
      <div className="bg-white dark:bg-slate-800 shadow-sm px-4 py-2 rounded-2xl max-w-[85%]">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {collapsed
            ? <ChevronRight className="w-3 h-3" />
            : <ChevronDown className="w-3 h-3" />}
          Actividad de herramientas ({lines.length})
        </button>
        {!collapsed && (
          <div
            ref={scrollRef}
            className="mt-1 max-h-32 overflow-y-auto chat-scrollbar text-xs font-mono text-slate-400 dark:text-slate-500 space-y-0.5"
            data-testid="live-activity"
          >
            {lines.map((a, i) => (
              <p key={i} className="leading-relaxed whitespace-pre-wrap break-words">› {a}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * CitationsModal (T-220) — modal de referencias/citas de un mensaje de la IA.
 * Muestra el texto citado (truncado ~200 chars), la fuente (hoja_fuente +
 * fecha) y hoja_destino si está disponible. Placeholder "(sin metadatos APA)"
 * cuando el campo apa viene vacío. Botón "Exportar CSV" al pie que descarga el
 * blob de /documents/{documentId}/citations/export (patrón EditorPage.js:
 * responseType blob + createObjectURL + <a download> + revokeObjectURL).
 * Sigue el patrón del ProposalCard local (componente en el mismo archivo).
 */
const CitationsModal = ({ open, onOpenChange, citations, documentId }) => {
  const [exporting, setExporting] = useState(false);

  // T-220: descarga CSV de las citas del documento (patrón blob de EditorPage).
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await axiosInstance.get(`/documents/${documentId}/citations/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `citas-${documentId}.csv`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Referencias</DialogTitle>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
          {citations.map((c, i) => {
            const texto = c.texto_citado
              ? (c.texto_citado.length > 200 ? c.texto_citado.slice(0, 197) + '…' : c.texto_citado)
              : '';
            const fuenteParts = [c.hoja_fuente, c.fecha].filter(Boolean);
            const fuente = fuenteParts.length > 0 ? fuenteParts.join(' · ') : '';
            const destino = c.hoja_destino ? ` → ${c.hoja_destino}` : '';
            return (
              <div key={c.id ?? i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
                {texto ? (
                  <p className="text-sm text-slate-700 dark:text-slate-200">&ldquo;{texto}&rdquo;</p>
                ) : (
                  <p className="text-sm italic text-slate-400 dark:text-slate-500">(sin texto citado)</p>
                )}
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {fuente ? `Fuente: ${fuente}${destino}` : '(sin fuente)'}
                </p>
                {!c.apa && (
                  <p className="mt-0.5 text-[11px] italic text-slate-400 dark:text-slate-500">
                    (sin metadatos APA)
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="h-8"
            data-testid="citations-export-csv"
          >
            {exporting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
            Exportar CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * MessageList component
 * Displays the chat messages with actions
 */
const MessageList = ({
  messages,
  loading,
  liveActivity,
  fontSize,
  copiedIndex,
  copyToClipboard,
  handleInsert,
  handleReplace,
  scrollRef,
  scrollToTop,
  scrollToBottom,
  onInsertTable,
  onApplyProposal,
  documentId,
}) => {
  const components = useMemo(
    () => onInsertTable ? createMarkdownComponents({ onInsertTable }) : markdownComponents,
    [onInsertTable]
  );

  // T-220: estado del modal de citas (citas seleccionadas o null). useState
  // local en MessageList — prop primitiva estable, no rompe el memo de T-155.
  const [citationsOpen, setCitationsOpen] = useState(null);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 chat-scrollbar"
        style={{ overscrollBehavior: 'none' }}
      >
        <div className="space-y-4 min-h-min">
          {messages.length === 0 && (
            <div className="text-center text-slate-500 dark:text-slate-400 mt-8">
              <p className="text-sm">Inicia una conversación</p>
              <p className="text-xs mt-2">El asistente puede ayudarte con tu documento</p>
            </div>
          )}
          {messages.map((msg, idx) => {
            const hasText = !!(msg.content && msg.content.trim());
            // T-150: omitir burbujas de asistente que no tienen nada que mostrar
            // (rondas de auto-continuación que solo llamaron herramientas y
            // devolvieron response vacío). React ignora los null del map; las
            // keys de los demás mensajes no cambian.
            if (msg.role === 'assistant'
              && !hasText
              && !(msg.tool_activity && msg.tool_activity.length > 0)
              && !msg.tools_unavailable
              && !msg.document_modified
              && !msg.edit_proposal
              && !(msg.edit_proposals && msg.edit_proposals.length)) {
              return null;
            }
            return (
            <div
              key={idx}
              data-testid={`chat-message-${idx}`}
              className={`flex group ${
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`relative max-w-[85%] ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-2xl px-4 py-2'
                    : 'bg-white dark:bg-slate-800 shadow-sm rounded-2xl px-4 py-3'
                }`}
                style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'anywhere',
                  minWidth: 0
                }}
              >
                {msg.role === 'assistant' && hasText && (
                  <div className="absolute -top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(msg.content, idx)}
                      className="h-6 w-6 p-0 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md shadow-md"
                      data-testid={`copy-message-${idx}`}
                      title="Copiar"
                    >
                      {copiedIndex === idx ? (
                        <CheckCheck className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-slate-600 dark:text-slate-300" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleInsert(msg.content)}
                      className="h-6 w-6 p-0 bg-white dark:bg-slate-700 hover:bg-green-50 dark:hover:bg-green-900/40 rounded-md shadow-md"
                      data-testid={`insert-message-${idx}`}
                      title="Insertar"
                    >
                      <Plus className="w-3 h-3 text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReplace(msg.content)}
                      className="h-6 w-6 p-0 bg-white dark:bg-slate-700 hover:bg-orange-50 dark:hover:bg-orange-900/40 rounded-md shadow-md"
                      data-testid={`replace-message-${idx}`}
                      title="Reemplazar"
                    >
                      <Replace className="w-3 h-3 text-orange-600" />
                    </Button>
                  </div>
                )}
                {msg.role === 'assistant' && msg.tool_activity && msg.tool_activity.length > 0 && (
                  <div className="mb-2 space-y-0.5">
                    {msg.tool_activity.map((a, i) => (
                      <p
                        key={i}
                        className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center gap-1"
                        data-testid={`tool-activity-${idx}-${i}`}
                      >
                        <Sparkles className="w-3 h-3" /> {a}
                      </p>
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' && msg.tools_unavailable && (
                  <p
                    className="mb-2 text-[11px] italic text-slate-400 dark:text-slate-500"
                    data-testid={`tools-unavailable-${idx}`}
                  >
                    El modelo no soporta herramientas — respuesta sin acceso al documento.
                  </p>
                )}
                {msg.role === 'assistant' && msg.document_modified && (
                  <p
                    className="mb-2 text-[11px] text-slate-400 dark:text-slate-500"
                    data-testid={`document-modified-${idx}`}
                  >
                    ✏️ El documento fue modificado por la IA
                  </p>
                )}
                {/* T-220: chip "N fuentes" — abre el modal de citas del mensaje. */}
                {msg.role === 'assistant' && msg.citations?.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setCitationsOpen(msg.citations)}
                    className="mb-2 text-[11px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1 cursor-pointer"
                    data-testid={`citations-chip-${idx}`}
                  >
                    <BookOpen className="w-3 h-3" /> {msg.citations.length} fuente{msg.citations.length === 1 ? '' : 's'}
                  </button>
                )}
                {msg.role === 'user' ? (
                  <p className="whitespace-pre-wrap" style={{ wordWrap: 'break-word', overflowWrap: 'anywhere', fontSize: fontSize }}>{msg.content}</p>
                ) : hasText ? (
                  <div className="prose prose-sm" style={{ wordWrap: 'break-word', overflowWrap: 'anywhere', fontSize: fontSize }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={components}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : null}
                {msg.role === 'assistant' && msg.edit_proposals && msg.edit_proposals.length > 0 && (
                  msg.edit_proposals.map((p, i) => (
                    <ProposalCard key={i} proposal={p} onApplyProposal={onApplyProposal} disabled={loading} />
                  ))
                )}
                {msg.role === 'assistant' && !msg.edit_proposals && msg.edit_proposal && (
                  <ProposalCard proposal={msg.edit_proposal} onApplyProposal={onApplyProposal} disabled={loading} />
                )}
                {/* T-204: métricas discretas de duración y créditos del job.
                    Solo viven en la sesión en vivo (como tool_activity); al
                    recargar el historial no aparecen (patrón existente).
                    Omite la parte que sea null; si ambas null, no pinta nada. */}
                {msg.role === 'assistant' && (() => {
                  const parts = [];
                  if (msg.duration_ms != null) {
                    parts.push(`⏱ ${(msg.duration_ms / 1000).toFixed(1)}s`);
                  }
                  if (msg.credits_charged != null) {
                    parts.push(`${Number(msg.credits_charged).toFixed(2)} créditos`);
                  }
                  if (parts.length === 0) return null;
                  return (
                    <p
                      className="mt-2 text-[10px] text-slate-400 dark:text-slate-500"
                      data-testid="msg-metrics"
                    >
                      {parts.join(' · ')}
                    </p>
                  );
                })()}
              </div>
            </div>
            );
          })}
          {loading && <ThinkingIndicator />}
          {loading && liveActivity && liveActivity.length > 0 && (
            <LiveActivity lines={liveActivity} />
          )}
        </div>
      </div>

      {/* Scroll Buttons */}
      {messages.length > 0 && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
          <Button
            data-testid="scroll-to-top"
            variant="ghost"
            size="sm"
            onClick={scrollToTop}
            className="h-9 w-9 p-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-lg hover:bg-sky-100 dark:hover:bg-slate-700 hover:shadow-xl rounded-full border border-slate-200 dark:border-slate-600 transition-all"
            title="Ir al principio"
          >
            <ArrowUp className="w-4 h-4 text-slate-700 dark:text-slate-200" />
          </Button>
          <Button
            data-testid="scroll-to-bottom"
            variant="ghost"
            size="sm"
            onClick={scrollToBottom}
            className="h-9 w-9 p-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-lg hover:bg-sky-100 dark:hover:bg-slate-700 hover:shadow-xl rounded-full border border-slate-200 dark:border-slate-600 transition-all"
            title="Ir al final"
          >
            <ArrowDown className="w-4 h-4 text-slate-700 dark:text-slate-200" />
          </Button>
        </div>
      )}

      {/* T-220: modal de citas/referencias del mensaje seleccionado. */}
      <CitationsModal
        open={!!citationsOpen}
        onOpenChange={(o) => { if (!o) setCitationsOpen(null); }}
        citations={citationsOpen || []}
        documentId={documentId}
      />
    </div>
  );
};

export default React.memo(MessageList);
